import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Upload, X, Image as ImageIcon, Sparkles } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import backgroundNoise from "@/assets/background-noise.svg";

const AdminPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "",
    author_name: "",
    author_title: "",
    read_time: "",
    hero_image: "",
    published: false,
  });

  useEffect(() => {
    checkAuth();
    if (id && id !== 'new') {
      fetchPost();
    }
  }, [id]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate('/admin');
      return;
    }

    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .eq('role', 'admin')
      .maybeSingle();

    if (!roles) {
      navigate('/admin');
    }
  };

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setFormData(data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch post",
        variant: "destructive",
      });
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls: string[]= [];

    try {
      for (const file of Array.from(files)) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError, data } = await supabase.storage
          .from('blog-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('blog-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setUploadedImages([...uploadedImages, ...uploadedUrls]);
      toast({
        title: "Success",
        description: `${uploadedUrls.length} image(s) uploaded successfully`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const copyImageUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({
      title: "Copied!",
      description: "Image URL copied to clipboard",
    });
  };

  const removeImage = async (url: string) => {
    try {
      const path = url.split('/blog-images/')[1];
      await supabase.storage.from('blog-images').remove([path]);
      
      setUploadedImages(uploadedImages.filter(img => img !== url));
      toast({
        title: "Removed",
        description: "Image removed successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const slug = formData.slug || generateSlug(formData.title);
      
      if (id && id !== 'new') {
        const { error } = await supabase
          .from('blog_posts')
          .update({ ...formData, slug })
          .eq('id', id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Post updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert([{ ...formData, slug }]);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Post created successfully",
        });
      }

      navigate('/admin/dashboard');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-background"
      style={{
        backgroundImage: `url(${backgroundNoise})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Clean Professional Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-5">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/dashboard')}
            className="hover:bg-muted font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="bg-white rounded-2xl shadow-lg border border-border p-8">
          {/* Header */}
          <div className="mb-8 pb-6 border-b border-border">
            <h1 className="text-3xl font-bold mb-2 text-foreground">
              {id === 'new' ? 'Create New Post' : 'Edit Post'}
            </h1>
            <p className="text-muted-foreground">Write and publish amazing content for your audience</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload Section */}
            <div className="bg-accent/5 border-2 border-accent/20 rounded-xl p-6">
              <Label className="text-base font-semibold mb-3 flex items-center gap-2 text-foreground">
                <ImageIcon className="w-5 h-5 text-accent" />
                Media Library
              </Label>
              <p className="text-sm text-muted-foreground mb-4">
                Upload images to use in your blog post. Click any image to copy its URL.
              </p>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
              
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="mb-4 border-2 hover:bg-accent/5 hover:border-accent font-medium"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading..." : "Upload Images"}
              </Button>

              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {uploadedImages.map((url, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={url} 
                        alt={`Upload ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg cursor-pointer hover:ring-2 hover:ring-accent transition-all border-2 border-border"
                        onClick={() => copyImageUrl(url)}
                        loading="lazy"
                        decoding="async"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(url)}
                        className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Form Fields */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-semibold text-foreground">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="h-12 bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20"
                placeholder="Enter a compelling title..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug" className="text-sm font-semibold text-foreground">Slug (auto-generated if left empty)</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="auto-generated-from-title"
                className="h-12 bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-semibold text-foreground">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="h-12 bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AI Marketing">AI Marketing</SelectItem>
                  <SelectItem value="Social Media">Social Media</SelectItem>
                  <SelectItem value="Digital Marketing">Digital Marketing</SelectItem>
                  <SelectItem value="SEO">SEO</SelectItem>
                  <SelectItem value="Content Marketing">Content Marketing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="excerpt" className="text-sm font-semibold text-foreground">Excerpt *</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={3}
                required
                className="bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20 resize-none"
                placeholder="A brief summary of your post..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content" className="text-sm font-semibold text-foreground">Content * (Markdown supported)</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={20}
                required
                className="bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20 font-mono text-sm"
                placeholder="Write your content in Markdown format..."
              />
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Tip: Use ![alt text](image-url) to insert images from your media library
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="author_name" className="text-sm font-semibold text-foreground">Author Name *</Label>
                <Input
                  id="author_name"
                  value={formData.author_name}
                  onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                  required
                  className="h-12 bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="author_title" className="text-sm font-semibold text-foreground">Author Title</Label>
                <Input
                  id="author_title"
                  value={formData.author_title}
                  onChange={(e) => setFormData({ ...formData, author_title: e.target.value })}
                  className="h-12 bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="read_time" className="text-sm font-semibold text-foreground">Read Time *</Label>
                <Input
                  id="read_time"
                  placeholder="5 min read"
                  value={formData.read_time}
                  onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                  required
                  className="h-12 bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hero_image" className="text-sm font-semibold text-foreground">Hero Image URL *</Label>
                <Input
                  id="hero_image"
                  placeholder="Paste from media library"
                  value={formData.hero_image}
                  onChange={(e) => setFormData({ ...formData, hero_image: e.target.value })}
                  required
                  className="h-12 bg-background border-2 border-border focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 p-4 bg-accent/5 border-2 border-accent/20 rounded-xl">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-5 h-5 rounded border-2 border-accent text-accent focus:ring-accent"
              />
              <Label htmlFor="published" className="cursor-pointer font-semibold flex items-center gap-2 text-foreground">
                <Sparkles className="w-4 h-4 text-accent" />
                Publish immediately
              </Label>
            </div>

            <div className="flex gap-4 pt-6">
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  "Save Post"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/dashboard')}
                className="border-2 font-medium"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminPostEditor;
